/* preLaunchStarter.js -- THE PRE-LAUNCH STARTER CHART (2026-11-02, the Pre-Launch batch S1; diffs/pre-launch-batch-build.txt).
 *
 * A PRE-LAUNCH company (utils/companyWindow.js isOneMonthStart) has no history to export, so the wizard offers these generic
 * income-statement and balance-sheet lines as starter templates (utils/templateGenerators.js generateISStarter /
 * generateBSStarter): the CANONICAL layout -- the spec's own columns (is_category, display_group, gl_line_name) and the
 * company's month columns -- with every amount 0. The operator keeps, renames or deletes lines, and fills only the balance
 * sheet's opening balances. The rows are Brian's list (2026-11-02), exactly. The spec gate (scripts/gen-onboarding-spec.mjs,
 * check 10) round-trips both files through their own parser and asserts every row classifies.
 *
 * PAYROLL ROWS are ZERO rows "Handled by Payroll" at Step 3, one block per department group, display_group = the group TEXT.
 * At Step 5c the Group Name must be typed exactly as that text (section 12.5 rule 132) so the history keys meet the department's
 * payroll lines: Customer Support (under COGS) / G&A / Sales / Marketing / Engineering (R&D).
 * The Sales block carries a Commissions row: a row stored as 'commissions' gives the Sales group its OWN Commissions line,
 * where an AE's commission books (utils/perDgPayrollActuals.js commissionLineKeys -- without it commission folds into Bonuses).
 * THE S&M VENDOR ROWS carry display_group "Marketing": S&M has two groups (Sales, Marketing), and a line with no group inside a
 * split section renders flat and raises P15 -- so they sit in the Marketing group.
 * Row shape: [is_category, display_group, gl_line_name] -- the spec's column order. */

export const PRE_LAUNCH_GROUP_NAMES = ['Customer Support', 'G&A', 'Sales', 'Marketing', 'Engineering'];

const payroll = (cat, group, extra = []) => ['Salaries', 'Payroll Taxes', 'Benefits', ...extra].map(n => [cat, group, n]);

export const PRE_LAUNCH_IS_ROWS = [
  ['Revenue', '', 'Subscription Revenue'],
  ['Revenue', '', 'Services Revenue'],
  ['Revenue', '', 'Other Revenue'],
  ['COGS', '', 'Hosting & Infrastructure'],
  ['COGS', '', 'Third-Party Software & APIs'],
  ['COGS', '', 'Merchant Fees'],
  ...payroll('COGS', 'Customer Support'),
  ...payroll('S&M', 'Sales', ['Commissions']),
  ...payroll('S&M', 'Marketing'),
  ['S&M', 'Marketing', 'Advertising'],
  ['S&M', 'Marketing', 'Events & Sponsorships'],
  ['S&M', 'Marketing', 'Sales & Marketing Software'],
  ...payroll('G&A', 'G&A'),
  ['G&A', '', 'Legal'],
  ['G&A', '', 'Accounting & Tax'],
  ['G&A', '', 'Rent'],
  ['G&A', '', 'Insurance'],
  ['G&A', '', 'Software'],
  ['G&A', '', 'Travel & Entertainment'],
  ['G&A', '', 'Office & Other'],
  ['G&A', '', 'Contractors'],
  ...payroll('R&D', 'Engineering'),
  ['R&D', '', 'Software & Tools'],
  ['R&D', '', 'Contractors'],
  ['Other', '', 'Interest Income'],
  ['Other', '', 'Income Taxes'],
  ['Other', '', 'Depreciation Expense'],
];

export const PRE_LAUNCH_BS_ROWS = [
  ['Current Assets', '', 'Operating Checking'],
  ['Current Assets', '', 'Savings / Money Market'],
  ['Current Assets', '', 'Accounts Receivable'],
  ['Current Assets', '', 'Prepaid Expenses'],
  ['Fixed Assets', '', 'Computer Equipment'],
  ['Fixed Assets', '', 'Accumulated Depreciation'],
  ['Current Liabilities', '', 'Accounts Payable'],
  ['Current Liabilities', '', 'Credit Card'],
  ['Current Liabilities', '', 'Accrued Expenses'],
  ['Current Liabilities', '', 'Accrued Payroll'],
  ['Current Liabilities', '', 'Deferred Revenue'],
  ['Equity', '', 'Common Stock'],
  ['Equity', '', 'Additional Paid-In Capital'],
  ['Equity', '', 'Retained Earnings'],
  ['Equity', '', 'Net Income'],
];

// The Instructions lines each starter carries (beside the spec's own notes).
export const PRE_LAUNCH_IS_NOTES = [
  'Pre-Launch starter: every amount is 0 (a Pre-Launch company has no history). Keep, rename or delete lines, and add any you need.',
  'Payroll rows (Salaries, Payroll Taxes, Benefits, Commissions) are "Handled by Payroll" at Step 3 -- confirm their pay types there.',
  // Batch 1 (2026-11-03, L3603): every group with its section, read from the rows above (one source)
  'At Step 5c type each department Group Name exactly as its display_group here -- ' + PRE_LAUNCH_GROUP_NAMES.length + ' groups: ' +
    PRE_LAUNCH_GROUP_NAMES.map(g => g + ' (' + (PRE_LAUNCH_IS_ROWS.find(r => r[1] === g) || [])[0] + ')').join(', ') + '.',
];
export const PRE_LAUNCH_BS_NOTES = [
  'Pre-Launch starter: fill only the opening balances (cash in Operating Checking and Savings / Money Market, and the matching Common Stock and Additional Paid-In Capital); leave every other line at 0.',
  'At Step 4 choose Savings / Money Market as the Sweep account (or your only bank account, if you have one).',
  'Deferred Revenue: keep it only if customers will pay in advance (subscriptions billed ahead) -- link it to that revenue stream at Step 7. If revenue is recognised as it is billed, delete the row: an unlinked deferred-revenue account blocks go-live.',
];
